/**
 * useNetworkRegain — run a callback when connectivity is regained.
 *
 * The HTTP transport-retry in `apiClient.ts` recovers requests that are *in
 * flight* when the network returns. But a screen whose fetch already failed and
 * settled during the outage has nothing left to re-trigger it. This hook lets a
 * screen refetch on the network-regain edge without the user pulling to refresh.
 *
 * Fires only while mounted; the handler ref is kept fresh so the effect never
 * needs to re-subscribe when the callback identity changes.
 *
 * See `docs/specs/mobile/network-resilience.mobile.md` (NETRES-005).
 */

import { useEffect, useRef } from 'react';
import { networkMonitor } from '../services/networkMonitor';

export function useNetworkRegain(handler: () => void): void {
  const handlerRef = useRef(handler);

  // Keep the ref current without re-subscribing when the callback identity
  // changes (ref writes belong in an effect, not in render).
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const unsubscribe = networkMonitor.onNetworkRegained(() => {
      handlerRef.current();
    });
    return unsubscribe;
  }, []);
}
